import { tokenVar } from '@hivewatch/dark'

import { ConnectionI } from '../types/types'

export type FloorPlanVisualVariant = 'legacy' | 'rebrand'
export type FloorPlanColorScheme = 'light' | 'dark'
export type AssociationVisualState =
  | 'ready'
  | 'associated'
  | 'grid-enabled'
  | 'tailgate-enabled'

export const FLOOR_PLAN_DEVICE_GLYPH_SIZE_RATIO = 14 / 24
export const FLOOR_PLAN_CHECKPOINT_ACTION_ICON_SIZE = 22
export const FLOOR_PLAN_CHECKPOINT_ACTION_OPACITY = {
  ACTIVE: 1,
  DISABLED: 0.3,
} as const

export const FLOOR_PLAN_ASSOCIATION_GLYPH_GEOMETRY = {
  cameraLeftRatio: 0.07,
  deviceLeftRatio: 0.51,
  sizeRatio: 0.42,
  topRatio: 0.29,
} as const

export const FLOOR_PLAN_CHECKPOINT_ACTION_ICON_IDS = {
  remove: '#floor-plan-checkpoint-remove-glyph',
  move: '#floor-plan-checkpoint-move-glyph',
  endMoving: '#floor-plan-checkpoint-end-moving-glyph',
} as const

const colorToken = (colorScheme: FloorPlanColorScheme, token: string) =>
  tokenVar(`colors.${colorScheme === 'light' ? 'light.' : ''}${token}`)

export const getAssociationVisualState = (
  connection: ConnectionI
): AssociationVisualState => {
  if (!connection.isConnected) return 'ready'
  if (connection.isTailgateEnabled) return 'tailgate-enabled'
  if (connection.isTailgateGridConfigured) return 'grid-enabled'
  return 'associated'
}

export const getAssociationVisualColors = (
  connection: ConnectionI,
  colorScheme: FloorPlanColorScheme
) => {
  const state = getAssociationVisualState(connection)
  const stateToken = state === 'ready' ? 'ready-to-associate' : state

  return {
    state,
    background: colorToken(colorScheme, `device.${stateToken}-bg`),
    glyph: colorToken(colorScheme, `device.${stateToken}-icon`),
    line: colorToken(colorScheme, `device.${stateToken}-bg`),
  }
}

export const getFloorPlanVisualColors = (
  colorScheme: FloorPlanColorScheme
) => ({
  canvas: colorToken(colorScheme, 'floorplan.canvas-bg'),
  emptyStateText: colorToken(colorScheme, 'text.primary'),
  markerBackground: colorToken(colorScheme, 'floorplan.marker-bg'),
  markerRing: colorToken(colorScheme, 'floorplan.marker-ring'),
  markerGlyph: colorToken(colorScheme, 'floorplan.marker-glyph'),
  coneFill: colorToken(colorScheme, 'floorplan.cone-fill'),
  coneFillFocused: colorToken(colorScheme, 'floorplan.cone-fill-focused'),
  coneEdge: colorToken(colorScheme, 'floorplan.cone-edge'),
  editFrame: colorToken(colorScheme, 'floorplan.edit-frame'),
  invalidPosition: colorToken(colorScheme, 'icon.danger'),
  checkpointActionHitArea: colorToken(
    colorScheme,
    'floorplan.checkpoint-action-hit-area'
  ),
  checkpointRemoveIcon: colorToken(colorScheme, 'icon.danger'),
  checkpointMoveIcon: colorToken(colorScheme, 'icon.active'),
  checkpointEndMovingIcon: colorToken(colorScheme, 'status.success-icon'),
  checkpointTooltipBackground: colorToken(colorScheme, 'bg.tooltip'),
  checkpointTooltipText: colorToken(colorScheme, 'text.primary'),
})

/**
 * Floor plans are effectively light surfaces, even when the surrounding app
 * uses the dark theme. Keep markers on the canvas on the higher-contrast dark
 * variant while allowing popovers and other contextual UI to use the selected
 * app theme through the generic helpers above.
 */
export const FLOOR_PLAN_CANVAS_MARKER_COLOR_SCHEME: FloorPlanColorScheme =
  'dark'

export const getFloorPlanCanvasMarkerColors = () =>
  getFloorPlanVisualColors(FLOOR_PLAN_CANVAS_MARKER_COLOR_SCHEME)

export const getFloorPlanCanvasAssociationColors = (connection: ConnectionI) =>
  getAssociationVisualColors(connection, FLOOR_PLAN_CANVAS_MARKER_COLOR_SCHEME)

export type FloorPlanVisualColors = ReturnType<typeof getFloorPlanVisualColors>
